import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { COURSES, api, REFERRAL_SOURCES, trackEvent } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, AlertCircle, Sparkles } from "lucide-react";

const PAYMENT_METHODS = [
  { value: "tikkie", label: "Tikkie", hint: "Snelle mobiele betaling" },
  { value: "bank_transfer", label: "Bankoverschrijving", hint: "Betalen via IBAN" },
  { value: "credit_card", label: "Creditcard", hint: "Veilige creditcard betaling" },
];

const PAYMENT_OPTIONS = [
  {
    value: "pay_in_full",
    label: "In één keer betalen",
    description: "Betaal het volledige cursusbedrag in één keer en reserveer je plek direct.",
  },
  {
    value: "deposit_remaining",
    label: "Aanbetaling + Restbedrag",
    description:
      "Reserveer je plek vandaag met een aanbetaling. Het resterende bedrag moet voor de cursusstart worden voldaan.",
    badge: "Meest gekozen",
  },
  {
    value: "payment_plan",
    label: "Betalingsregeling",
    description:
      "Flexibele betalingsregeling. Na je aanmelding nemen we contact op om de mogelijkheden te bespreken.",
  },
];

const initialForm = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  instagram: "",
  can_braid: null,
  can_weave: null,
  goal: "",
  want_to_learn: "",
  heard_about: "",
  dietary_preferences: "",
  food_dislikes: "",
  allergies: "",
  payment_method: "",
  payment_option: "",
  agreed_price: false,
  agreed_payment_terms: false,
};

function validate(form, course) {
  const errors = {};
  if (!form.first_name.trim()) errors.first_name = "Vul je voornaam in";
  if (!form.last_name.trim()) errors.last_name = "Vul je achternaam in";
  if (!form.phone.trim()) errors.phone = "Vul een geldig telefoonnummer in";
  else if (!/^[+\d\s()-]{6,}$/.test(form.phone.trim())) errors.phone = "Vul een geldig telefoonnummer in";
  if (!form.email.trim()) errors.email = "Vul je e-mailadres in";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = "Vul een geldig e-mailadres in";
  if (form.can_braid === null) errors.can_braid = "Beantwoord deze vraag";
  if (form.can_weave === null) errors.can_weave = "Beantwoord deze vraag";
  if (!form.goal.trim()) errors.goal = "Vertel ons je doel met de cursus";
  if (course?.requiresBraidingSkill && form.can_braid === false) {
    errors.can_braid = "Voor deze cursus zijn basis vlechtvaardigheden vereist";
  }
  if (!form.payment_method) errors.payment_method = "Kies een betaalmethode";
  if (!form.payment_option) errors.payment_option = "Kies een betaaloptie";
  if (!form.agreed_price) errors.agreed_price = "Bevestig de cursusprijs";
  if (!form.agreed_payment_terms) errors.agreed_payment_terms = "Bevestig de betalingsvoorwaarden";
  return errors;
}

export default function Registration() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const course = COURSES[courseId];

  const [form, setForm] = useState(initialForm);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const firstNameRef = useRef(null);
  const startedRef = useRef(false);
  const emailTrackedRef = useRef(false);
  const paymentMethodTrackedRef = useRef(false);

  useEffect(() => {
    // Auto-focus first name input when the form opens
    const t = setTimeout(() => {
      firstNameRef.current?.focus({ preventScroll: false });
    }, 80);
    trackEvent("registration_form_start", { metadata: { course_id: courseId } });
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!course) {
    return (
      <Layout>
        <p className="pt-8">Course not found.</p>
        <Link className="underline text-sm" to="/">Go back</Link>
      </Layout>
    );
  }

  const errors = validate(form, course);
  const isValid = Object.keys(errors).length === 0;

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Track once when email first has a valid @ character (proxy for "email entered")
    if (k === "email" && !emailTrackedRef.current && v.includes("@") && v.includes(".")) {
      emailTrackedRef.current = true;
      trackEvent("registration_email_entered");
    }
    if (k === "payment_method" && v && !paymentMethodTrackedRef.current) {
      paymentMethodTrackedRef.current = true;
      trackEvent("payment_method_selected", { metadata: { method: v } });
      if (v === "bank_transfer") trackEvent("bank_transfer_selected");
    }
  };
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));
  const errorFor = (k) => (touched[k] || showAllErrors ? errors[k] : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      setShowAllErrors(true);
      setTimeout(() => {
        document.querySelector('[data-has-error="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/register", { course_id: courseId, ...form });
      toast.success("Aanmelding ontvangen");
      if (data.redirect_to_payment && data.payment_url) {
        trackEvent("tikkie_redirect_clicked", { order_id: data.order_id });
        window.location.href = data.payment_url;
      } else {
        navigate(`/confirmation/${data.order_id}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Er ging iets mis. Probeer het opnieuw.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout step={2}>
      <button
        onClick={() => navigate("/")}
        className="flex items-center gap-2 label-tag mb-6 mt-2 hover:opacity-70"
        data-testid="back-button"
      >
        <ArrowLeft size={14} /> Terug naar cursussen
      </button>

      <div className="label-tag mb-3">— Stap 02 / Aanmelding</div>
      <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] tracking-tight">
        Reserveer<br />je <span className="italic">plek</span>
      </h1>

      <div className="mt-7 glass-block">
        <div className="label-tag opacity-80">Gekozen cursus</div>
        <div className="font-display text-3xl sm:text-4xl mt-1 text-[#FF6A1A]">{course.name}</div>
        <div className="mt-3 flex items-center justify-between text-[#FF6A1A]/85 text-sm font-medium">
          <span>3-daags intensief · 11:00 – 18:00</span>
          <span className="font-display text-2xl text-[#FF6A1A]">€{course.price.toLocaleString("en-US")}</span>
        </div>
      </div>

      <p className="mt-6 text-sm text-black/70 font-medium">
        Velden met een <span className="text-red-700 font-bold">*</span> zijn verplicht.
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-9" data-testid="registration-form" noValidate>
        <Section title="01 — Persoonlijke gegevens">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <Field label="Voornaam" required error={errorFor("first_name")}>
              <input
                ref={firstNameRef}
                type="text"
                value={form.first_name}
                onChange={(e) => update("first_name", e.target.value)}
                onBlur={() => markTouched("first_name")}
                className={`form-input ${errorFor("first_name") ? "border-red-700 bg-red-50/40" : ""}`}
                placeholder="Je voornaam"
                autoFocus
                data-testid="input-first-name"
              />
            </Field>
            <Field label="Achternaam" required error={errorFor("last_name")}>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => update("last_name", e.target.value)}
                onBlur={() => markTouched("last_name")}
                className={`form-input ${errorFor("last_name") ? "border-red-700 bg-red-50/40" : ""}`}
                placeholder="Je achternaam"
                data-testid="input-last-name"
              />
            </Field>
            <Field label="Telefoon" required error={errorFor("phone")}>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                onBlur={() => markTouched("phone")}
                className={`form-input ${errorFor("phone") ? "border-red-700 bg-red-50/40" : ""}`}
                placeholder="+31 6 12 34 56 78"
                data-testid="input-phone"
              />
            </Field>
            <Field label="E-mail" required error={errorFor("email")}>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                onBlur={() => markTouched("email")}
                className={`form-input ${errorFor("email") ? "border-red-700 bg-red-50/40" : ""}`}
                placeholder="jij@mail.com"
                data-testid="input-email"
              />
            </Field>
            <Field label="Instagram" className="sm:col-span-2">
              <input
                type="text"
                value={form.instagram}
                onChange={(e) => update("instagram", e.target.value)}
                className="form-input"
                placeholder="@jouwhandle"
                data-testid="input-instagram"
              />
            </Field>
          </div>
        </Section>

        <Section title="02 — Vaardigheden">
          <div className="space-y-6">
            <YesNo
              label="Kun je vlechten?"
              required
              value={form.can_braid}
              onChange={(v) => { update("can_braid", v); markTouched("can_braid"); }}
              testId="can-braid"
              error={errorFor("can_braid")}
            />
            <YesNo
              label="Heb je al ervaring met hair installs?"
              required
              value={form.can_weave}
              onChange={(v) => { update("can_weave", v); markTouched("can_weave"); }}
              testId="can-weave"
              error={errorFor("can_weave")}
            />
          </div>
        </Section>

        <Section title="03 — Jouw doel">
          <div className="space-y-5">
            <Field label="Wat is je doel met deze cursus?" required error={errorFor("goal")}>
              <textarea
                rows={3}
                value={form.goal}
                onChange={(e) => update("goal", e.target.value)}
                onBlur={() => markTouched("goal")}
                className={`form-input ${errorFor("goal") ? "border-red-700 bg-red-50/40" : ""}`}
                placeholder="Eigen bedrijf starten, nieuwe vaardigheid leren ..."
                data-testid="input-goal"
              />
            </Field>
            <Field label="Wat wil je leren van Pattern Texture?">
              <textarea
                rows={3}
                value={form.want_to_learn}
                onChange={(e) => update("want_to_learn", e.target.value)}
                className="form-input"
                placeholder="De PTR methode, een schone leave-out, platte installs ..."
                data-testid="input-want-to-learn"
              />
            </Field>
            <Field label="Hoe heb je over Pattern Texture gehoord?">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {REFERRAL_SOURCES.map((src) => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => update("heard_about", src)}
                    className={`toggle-pill !py-2.5 !text-[13px] ${form.heard_about === src ? "active" : ""}`}
                    data-testid={`referral-${src.toLowerCase().replace(/ /g, "-")}`}
                  >
                    {src}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        </Section>

        <Section title="04 — Eten & voorkeuren">
          <p className="text-sm text-black/70 mb-4 -mt-1">
            We zorgen voor eten & drinken tijdens alle cursusdagen. Laat ons je voorkeuren weten.
          </p>
          <div className="space-y-4">
            <Field label="Heb je dieetvoorkeuren?">
              <input
                type="text"
                value={form.dietary_preferences}
                onChange={(e) => update("dietary_preferences", e.target.value)}
                className="form-input"
                placeholder="Vegetarisch, halal, vegan, geen ..."
                data-testid="input-dietary"
              />
            </Field>
            <Field label="Zijn er dingen die je niet eet?">
              <input
                type="text"
                value={form.food_dislikes}
                onChange={(e) => update("food_dislikes", e.target.value)}
                className="form-input"
                placeholder="Iets dat we moeten vermijden?"
                data-testid="input-food-dislikes"
              />
            </Field>
            <Field label="Heb je allergieën waar we rekening mee moeten houden?">
              <input
                type="text"
                value={form.allergies}
                onChange={(e) => update("allergies", e.target.value)}
                className="form-input"
                placeholder="Noten, zuivel, gluten ..."
                data-testid="input-allergies"
              />
            </Field>
          </div>
        </Section>

        <Section title="05 — Kies je betaalmethode">
          <p className="text-sm text-black/70 mb-4 -mt-1">
            Hoe wil je betalen? Kies de methode die voor jou het beste werkt.
          </p>
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-2.5"
            data-has-error={errorFor("payment_method") ? "true" : undefined}
            data-testid="payment-method-group"
          >
            {PAYMENT_METHODS.map((m) => {
              const active = form.payment_method === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => { update("payment_method", m.value); markTouched("payment_method"); }}
                  className={`text-left rounded-2xl border-2 px-4 py-3 transition-all ${
                    active
                      ? "bg-black text-[#FF6A1A] border-black shadow-[0_8px_20px_-8px_rgba(0,0,0,0.4)]"
                      : "bg-white/45 backdrop-blur border-black/20 text-black hover:border-black/40"
                  }`}
                  data-testid={`payment-method-${m.value}`}
                  aria-pressed={active}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        active ? "border-[#FF6A1A]" : "border-black/40"
                      }`}
                    >
                      {active && <span className="w-2 h-2 rounded-full bg-[#FF6A1A]" />}
                    </span>
                    <span className="font-semibold text-sm">{m.label}</span>
                  </div>
                  <div className={`mt-1.5 text-[11px] leading-snug ${active ? "text-[#FF6A1A]/80" : "text-black/65"}`}>
                    {m.hint}
                  </div>
                </button>
              );
            })}
          </div>
          {errorFor("payment_method") && (
            <span className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-red-700">
              <AlertCircle size={12} strokeWidth={2.5} />
              {errorFor("payment_method")}
            </span>
          )}
        </Section>

        <Section title="06 — Kies je betaaloptie">
          <p className="text-sm text-black/70 mb-4 -mt-1">
            Kies de optie die past bij jouw budget.
          </p>
          <div
            className="space-y-3"
            data-has-error={errorFor("payment_option") ? "true" : undefined}
            data-testid="payment-option-group"
          >
            {PAYMENT_OPTIONS.map((o) => {
              const active = form.payment_option === o.value;
              const recommended = o.badge;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { update("payment_option", o.value); markTouched("payment_option"); }}
                  className={`w-full text-left rounded-2xl border-2 px-4 py-4 transition-all relative ${
                    active
                      ? "bg-black text-[#FF6A1A] border-black shadow-[0_10px_24px_-10px_rgba(0,0,0,0.45)]"
                      : recommended
                        ? "bg-[#FF6A1A]/15 backdrop-blur border-[#FF6A1A]/60 text-black hover:bg-[#FF6A1A]/25"
                        : "bg-white/45 backdrop-blur border-black/20 text-black hover:border-black/40"
                  }`}
                  data-testid={`payment-option-${o.value}`}
                  aria-pressed={active}
                >
                  {recommended && (
                    <span
                      className="absolute -top-2 right-3 inline-flex items-center gap-1 bg-[#FF6A1A] text-black text-[10px] font-bold tracking-wide uppercase px-2.5 py-0.5 rounded-full shadow-[0_4px_10px_-2px_rgba(255,106,26,0.6)]"
                      data-testid="most-popular-badge"
                    >
                      <Sparkles size={10} strokeWidth={2.5} /> {recommended}
                    </span>
                  )}
                  <div className="flex items-start gap-3">
                    <span
                      className={`w-4 h-4 mt-1 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        active ? "border-[#FF6A1A]" : "border-black/40"
                      }`}
                    >
                      {active && <span className="w-2 h-2 rounded-full bg-[#FF6A1A]" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-base">{o.label}</div>
                      <div className={`mt-1 text-[13px] leading-snug ${active ? "text-[#FF6A1A]/85" : "text-black/70"}`}>
                        {o.description}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {errorFor("payment_option") && (
            <span className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-red-700">
              <AlertCircle size={12} strokeWidth={2.5} />
              {errorFor("payment_option")}
            </span>
          )}
        </Section>

        <Section title="07 — Akkoord">
          <div className="space-y-4">
            <Checkbox
              checked={form.agreed_price}
              onChange={(v) => { update("agreed_price", v); markTouched("agreed_price"); }}
              label="Ik ga akkoord met de cursusprijs"
              required
              testId="agree-price"
              error={errorFor("agreed_price")}
            />
            <Checkbox
              checked={form.agreed_payment_terms}
              onChange={(v) => { update("agreed_payment_terms", v); markTouched("agreed_payment_terms"); }}
              label={
                <span>
                  Ik begrijp dat betaling vereist is om mijn plek te reserveren en ik accepteer de{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-semibold hover:opacity-80"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="terms-link-form"
                  >
                    Algemene Voorwaarden
                  </a>
                </span>
              }
              required
              testId="agree-terms"
              error={errorFor("agreed_payment_terms")}
            />
          </div>
        </Section>

        {showAllErrors && !isValid && (
          <div className="rounded-2xl bg-red-100 border-2 border-red-700 px-4 py-3 flex items-start gap-2 text-sm font-medium text-red-900">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <span>Vul alle verplichte velden hierboven in voordat je doorgaat.</span>
          </div>
        )}

        {/* Payment due preview + warning */}
        {form.payment_option && (
          <div
            className="rounded-2xl bg-[#FF6A1A]/15 border border-[#FF6A1A]/50 px-5 py-4 flex items-start gap-3"
            data-testid="payment-due-preview"
          >
            <Sparkles size={18} strokeWidth={2} className="shrink-0 mt-0.5 text-[#FF6A1A]" />
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-black/70">Je betaalt nu</div>
              <div className="mt-0.5 font-display text-3xl text-[#FF6A1A] leading-none">
                €{computeDue(course.price, form.payment_option).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="mt-2 text-xs font-medium text-black/75 leading-snug">
                {dueHelperText(form.payment_option, course.price)}
              </p>
            </div>
          </div>
        )}

        <div
          className="rounded-2xl bg-black/90 text-[#FF6A1A] px-4 py-3 flex items-start gap-2 text-sm font-medium leading-snug"
          data-testid="payment-warning"
        >
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <span>
            Na het versturen van je aanmelding word je doorgestuurd om de betaling af te ronden.
            <strong className="block mt-0.5">Je plek is pas gereserveerd na betaling.</strong>
          </span>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="btn-primary mt-6 flex items-center justify-center gap-3"
          data-testid="continue-button"
        >
          {submitting && <Loader2 className="animate-spin" size={16} />}
          <span>{submitting ? "Bezig…" : "Aanmelden & Betalen →"}</span>
        </button>
      </form>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="section-title">{title}</div>
      {children}
    </div>
  );
}

function computeDue(price, option) {
  if (option === "pay_in_full") return Number(price);
  if (option === "deposit_remaining") return Math.round((Number(price) / 2) * 100) / 100;
  if (option === "payment_plan") return Math.round((Number(price) / 3) * 100) / 100;
  return Number(price);
}

function dueHelperText(option, price) {
  const remaining = Number(price) - computeDue(price, option);
  if (option === "pay_in_full") return "Volledige cursusprijs — je plek is direct gereserveerd na betaling.";
  if (option === "deposit_remaining") {
    return `50% aanbetaling reserveert je plek. Resterende €${remaining.toFixed(2)} is verschuldigd vóór de cursusstart.`;
  }
  if (option === "payment_plan") {
    return `Eerste termijn van 3. We nemen contact op over termijn 2 en 3 (€${(computeDue(price, option)).toFixed(2)} per termijn).`;
  }
  return "";
}

function Field({ label, required, error, children, className = "" }) {
  return (
    <label className={`block ${className}`} data-has-error={error ? "true" : undefined}>
      <span className="block text-xs font-semibold mb-2 tracking-wide uppercase">
        {label}
        {required && <span className="text-red-700 ml-1">*</span>}
      </span>
      {children}
      {error && (
        <span className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-red-700">
          <AlertCircle size={12} strokeWidth={2.5} />
          {error}
        </span>
      )}
    </label>
  );
}

function YesNo({ label, required, value, onChange, testId, error }) {
  return (
    <div data-has-error={error ? "true" : undefined}>
      <span className="block text-sm font-semibold mb-3">
        {label}
        {required && <span className="text-red-700 ml-1">*</span>}
      </span>
      <div className="grid grid-cols-2 gap-3">
        {[true, false].map((opt) => (
          <button
            key={String(opt)}
            type="button"
            onClick={() => onChange(opt)}
            className={`toggle-pill ${value === opt ? "active" : ""} ${error ? "!border-red-700" : ""}`}
            data-testid={`${testId}-${opt ? "yes" : "no"}`}
          >
            {opt ? "Ja" : "Nee"}
          </button>
        ))}
      </div>
      {error && (
        <span className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-red-700">
          <AlertCircle size={12} strokeWidth={2.5} />
          {error}
        </span>
      )}
    </div>
  );
}

function Checkbox({ checked, onChange, label, required, testId, error }) {
  return (
    <div data-has-error={error ? "true" : undefined}>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className="flex items-start gap-3 text-left w-full"
        data-testid={`checkbox-${testId}`}
      >
        <span
          className={`w-6 h-6 shrink-0 mt-0.5 rounded-md flex items-center justify-center transition-all ${
            checked
              ? "bg-black shadow-[0_4px_12px_-4px_rgba(120,40,0,0.45)]"
              : error
                ? "bg-white/40 border-2 border-red-700 backdrop-blur"
                : "bg-white/40 border border-black/30 backdrop-blur"
          }`}
        >
          {checked && (
            <svg viewBox="0 0 16 16" className="w-4 h-4 text-[#FF6A1A]" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M3 8.5l3.5 3.5L13 5" />
            </svg>
          )}
        </span>
        <span className="text-sm leading-snug pt-0.5 font-medium">
          {label}
          {required && <span className="text-red-700 ml-1">*</span>}
        </span>
      </button>
      {error && (
        <span className="mt-1.5 ml-9 flex items-center gap-1.5 text-xs font-semibold text-red-700">
          <AlertCircle size={12} strokeWidth={2.5} />
          {error}
        </span>
      )}
    </div>
  );
}
